import React from "react";

import { OrderDetail, OrderItem } from "../../types/orders";

interface OrderItemsProps {
  order: OrderDetail;
  formatCurrency: (amount: number) => string;
}

const OrderItems: React.FC<OrderItemsProps> = ({ order, formatCurrency }) => {
  // Group selections by half (for split items)
  const getSelectionsForHalf = (
    item: OrderItem,
    halfType?: "first" | "second",
  ) => {
    if (!item.selections) return [];

    // If no halves, return all selections
    if (!item.halves || item.halves.length === 0) {
      return item.selections;
    }

    // Filter selections for the specific half
    return item.selections.filter((selection) => {
      const half = item.halves?.find((h) => h.half_type === halfType);

      if (!half) return false;

      // Check if this selection belongs to this half
      // This would depend on your data structure
      // Here we're assuming selections have a half_id property
      return selection.half_id === half.id;
    });
  };

  // Get total price breakdown
  const getTotalBreakdown = () => {
    return (
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
        <div className="flex justify-between py-1">
          <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
          <span className="text-gray-900 dark:text-white">
            {formatCurrency(order.subtotal)}
          </span>
        </div>
        <div className="flex justify-between py-1">
          <span className="text-gray-600 dark:text-gray-400">Tax</span>
          <span className="text-gray-900 dark:text-white">
            {formatCurrency(order.tax)}
          </span>
        </div>
        {order.delivery_fee > 0 && (
          <div className="flex justify-between py-1">
            <span className="text-gray-600 dark:text-gray-400">
              Delivery Fee
            </span>
            <span className="text-gray-900 dark:text-white">
              {formatCurrency(order.delivery_fee)}
            </span>
          </div>
        )}
        {order.tip > 0 && (
          <div className="flex justify-between py-1">
            <span className="text-gray-600 dark:text-gray-400">Tip</span>
            <span className="text-gray-900 dark:text-white">
              {formatCurrency(order.tip)}
            </span>
          </div>
        )}
        <div className="flex justify-between py-2 font-semibold text-lg mt-2 border-t border-gray-200 dark:border-gray-700">
          <span className="text-gray-900 dark:text-white">Total</span>
          <span className="text-gray-900 dark:text-white">
            {formatCurrency(order.total)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
          Order Items
        </h3>
      </div>

      <div className="p-6">
        {/* Order Items List */}
        <div className="space-y-6">
          {order.items.map((item) => (
            <div
              key={item.id}
              className="flex flex-col md:flex-row gap-4 pb-4 border-b border-gray-200 dark:border-gray-700 last:border-b-0 last:pb-0"
            >
              {/* Item details */}
              <div className="flex-1">
                <div className="flex justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white text-base">
                      {item.quantity > 1 && (
                        <span className="mr-2">{item.quantity}x</span>
                      )}
                      {item.product_name}
                      {item.product_type_name && ` - ${item.product_type_name}`}
                    </h4>
                    {item.is_split && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Split pizza (half & half)
                      </p>
                    )}
                  </div>
                  <div className="text-gray-900 dark:text-white font-medium">
                    {formatCurrency(item.total_price)}
                  </div>
                </div>

                {/* Special instructions */}
                {item.special_instructions && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Special Instructions:</span>{" "}
                      {item.special_instructions}
                    </p>
                  </div>
                )}

                {/* Selections/toppings */}
                {item.is_split ? (
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    {/* First half */}
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        First Half:
                      </p>
                      {item.halves && item.halves[0]?.description && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                          {item.halves[0].description}
                        </p>
                      )}
                      <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside">
                        {getSelectionsForHalf(item, "first").map(
                          (selection, i) => (
                            <li key={i}>
                              {selection.quantity > 1 &&
                                `${selection.quantity}x `}
                              {selection.name ||
                                `Selection #${selection.selection_id}`}
                              {selection.price > 0 &&
                                ` (+${formatCurrency(selection.price)})`}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>

                    {/* Second half */}
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Second Half:
                      </p>
                      {item.halves && item.halves[1]?.description && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                          {item.halves[1].description}
                        </p>
                      )}
                      <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside">
                        {getSelectionsForHalf(item, "second").map(
                          (selection, i) => (
                            <li key={i}>
                              {selection.quantity > 1 &&
                                `${selection.quantity}x `}
                              {selection.name ||
                                `Selection #${selection.selection_id}`}
                              {selection.price > 0 &&
                                ` (+${formatCurrency(selection.price)})`}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  </div>
                ) : (
                  /* Regular item selections */
                  item.selections &&
                  item.selections.length > 0 && (
                    <div className="mt-2">
                      <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside">
                        {item.selections.map((selection, i) => (
                          <li key={i}>
                            {selection.quantity > 1 &&
                              `${selection.quantity}x `}
                            {selection.name ||
                              `Selection #${selection.selection_id}`}
                            {selection.price > 0 &&
                              ` (+${formatCurrency(selection.price)})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Price breakdown */}
        {getTotalBreakdown()}
      </div>
    </div>
  );
};

export default OrderItems;
